const toLocalDateTime = (dateValue, timeValue) => {
  if (!dateValue || !timeValue) return null;
  const [year, month, day] = String(dateValue).split('-').map(Number);
  const [hours, minutes] = String(timeValue).split(':').map(Number);
  if ([year, month, day, hours, minutes].some((value) => Number.isNaN(value))) return null;
  return new Date(year, month - 1, day, hours, minutes, 0, 0);
};

const monthIndex = (year, month) => year * 12 + month - 1;

const compareServiceDateTime = (left, right) => {
  const leftDate = toLocalDateTime(left.service_date, left.start_time) || new Date(0);
  const rightDate = toLocalDateTime(right.service_date, right.start_time) || new Date(0);
  return leftDate - rightDate || String(left.id || '').localeCompare(String(right.id || ''));
};

/**
 * Service times are stored as a local service_date plus HH:mm. Keep the
 * combination in one place so the UI never mixes date-only and datetime
 * comparisons. `now` is injectable for deterministic tests.
 */
export const getServiceTemporalState = (service, now = new Date()) => {
  const endDateTime = toLocalDateTime(service?.service_date, service?.end_time);
  return endDateTime && endDateTime < now ? 'historical' : 'upcoming';
};

export const getMonthRelation = (year, month, now = new Date()) => {
  const selected = monthIndex(year, month);
  const current = monthIndex(now.getFullYear(), now.getMonth() + 1);
  if (selected < current) return 'previous';
  if (selected > current) return 'future';
  return 'current';
};

/**
 * Partition the complete API response for presentation only. No service is
 * removed from the response, so summary counts and workload remain intact.
 */
export const partitionRosterServices = (services = [], { year, month, now = new Date() } = {}) => {
  const relation = getMonthRelation(year ?? now.getFullYear(), month ?? now.getMonth() + 1, now);
  const upcoming = [];
  const historical = [];

  services.forEach((service) => {
    if (relation === 'previous' || (relation === 'current' && getServiceTemporalState(service, now) === 'historical')) {
      historical.push(service);
    } else {
      upcoming.push(service);
    }
  });

  return {
    mode: relation,
    upcoming: upcoming.sort(compareServiceDateTime),
    historical: historical.sort((left, right) => compareServiceDateTime(right, left)),
  };
};

export const shiftRosterMonth = (year, month, delta) => {
  const next = monthIndex(year, month) + delta;
  return { year: Math.floor(next / 12), month: (next % 12 + 12) % 12 + 1 };
};

export const formatRosterMonth = (year, month) => new Intl.DateTimeFormat('nl-NL', {
  month: 'long',
  year: 'numeric',
}).format(new Date(year, month - 1, 1));

export const formatRosterServiceDate = (value) => {
  if (!value) return '-';
  return new Intl.DateTimeFormat('nl-NL', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(new Date(`${value}T12:00:00`));
};

